import { useLocale } from 'next-intl';
import React, { useRef } from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { getCssVar } from '@cineamo/ui/src/helpers/color-helper';
import { getShowingPropertiesDimension, getShowingPropertiesLanguage } from '@cineamo/ui/src/helpers/showing-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { Icon3DGlasses16 } from '@cineamo/ui/src/icons/ic-3dGlasses';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';
import { AriaHiddenEmoji } from '@cineamo/ui/src/misc/AriaHiddenEmoji';
import { TicketButtonSimple, TicketButtonSimpleStyle } from '@cineamo/ui/src/ticket-shape/TicketButtonSimple';

import { useShowtimesFinder } from '../providers/ShowtimesFinderProvider';

import { PREVIEWS_SELECTED_DATE } from './previewsUtils';

const ShowtimesFinderTicketButton: React.FC<{
    showing: ShowingDto;
    cinema: CinemaDto;
    viewType: 'list' | 'map';
    selectedDate: string;
    purchaseReason?: PurchaseReasonEnum;
    showShowingProperties?: boolean;
}> = ({ showing, cinema, viewType, selectedDate, purchaseReason, showShowingProperties }) => {
    const format = useFormat();
    const locale = useLocale();
    const ref = useRef<HTMLDivElement>(null);
    const activeTab = useShowtimesFinder()?.activeTab;

    // eslint-disable-next-line react-hooks/refs -- intentional: falls back to document.body before mount; value is re-read each render as inherited CSS vars may change
    const surfaceColor = getCssVar(ref.current ?? document.body, '--surface-color');
    // eslint-disable-next-line react-hooks/refs -- intentional: falls back to document.body before mount; value is re-read each render as inherited CSS vars may change
    const onSurfaceColor = getCssVar(ref.current ?? document.body, '--on-surface-color');
    const style =
        surfaceColor && onSurfaceColor
            ? {
                  backgroundColor: surfaceColor,
                  color: onSurfaceColor
              }
            : TicketButtonSimpleStyle.OnDark;

    const showingPropertiesDimension = getShowingPropertiesDimension(showing);
    const showingPropertiesLanguage = getShowingPropertiesLanguage(showing, locale);
    const showingPropertyLabels = [
        showingPropertiesDimension ? (
            <span className="inline-flex flex-row items-center space-x-4">
                <Icon3DGlasses16 className="flex-none" />
                <span>{showingPropertiesDimension}</span>
            </span>
        ) : null,
        showingPropertiesLanguage.length > 0 ? (
            <span className="opacity-70">
                <AriaHiddenEmoji text={showingPropertiesLanguage.join(' ')} />
            </span>
        ) : null
    ].flatMap((label) => (label ? [label] : []));
    const showProperties = showShowingProperties && showingPropertyLabels.length > 0;

    return (
        <div ref={ref}>
            <TicketButtonSimple
                style={style}
                showing={showing}
                cornerRadius={8}
                trackingData={{
                    viewType: viewType,
                    cinemaName: cinema.name,
                    cinemaCity: cinema.city,
                    ...(activeTab ? { activeTab } : {})
                }}
                purchaseReason={purchaseReason ?? PurchaseReasonEnum.MOVIE_LANDINGPAGE}
                onlineTicketUrl={showing.ticketUrls?.default ?? showing.onlineTicketUrl}
                ticketHeight={showProperties ? 'auto' : 48}>
                <span
                    className={cn(
                        'min-w-[80px] px-16 py-6',
                        showProperties
                            ? 'flex flex-col items-center justify-center space-y-2'
                            : 'flex flex-row space-x-8'
                    )}
                    style={showProperties ? { minHeight: 48 } : undefined}>
                    <span className="flex flex-row space-x-8">
                        {selectedDate === 'all' || selectedDate === PREVIEWS_SELECTED_DATE ? (
                            <span>{format.dateTime.formatDayMonth(showing.startDatetime)}</span>
                        ) : null}
                        <span>{format.dateTime.formatTime(showing.startDatetime)}</span>
                    </span>
                    {showProperties && (
                        <DotSeparatedLabel
                            className="flex items-center text-body-bold-10"
                            classNameLabel="flex items-center"
                            labels={showingPropertyLabels}
                        />
                    )}
                </span>
            </TicketButtonSimple>
        </div>
    );
};

export default ShowtimesFinderTicketButton;
